import React, { useState, useEffect, useRef, useCallback } from 'react';
import { format } from 'date-fns';
import { taskAPI, timeLogAPI, commentAPI, fileAPI, customFieldAPI, bugAPI } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import {
    Dialog,
    DialogContent,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import { Calendar } from './ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { ScrollArea } from './ui/scroll-area';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from './ui/select';
import { cn, priorityConfig, getInitials } from '../lib/utils';
import RichTextEditor from './RichTextEditor';
import {
    X,
    Calendar as CalendarIcon,
    Clock,
    User,
    Users,
    Tag,
    Plus,
    Trash2,
    Loader2,
    Flag,
    Edit2,
    Paperclip,
    FileImage,
    FileVideo,
    File,
    Upload,
    Check,
    Search,
    ChevronDown,
    ChevronRight,
    Bug,
    Eye,
    ListChecks,
    AlertTriangle,
    CheckCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { DialogClose } from '@radix-ui/react-dialog';
import FieldStatus from './Field/FieldStatus';

// Task Categories
const taskCategories = ['BUG', 'TASK', 'CHANGE_REQUEST', 'IMPROVEMENT', 'FEATURE', 'DAILY_TASK'];

// Assignee Multi Select Component
const AssigneeMultiSelect = ({ members, selectedIds, onChange, disabled }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const dropdownRef = useRef(null);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Normalize member data - support both formats (id/name/email and user_id/user_name/user_email)
    const normalizedMembers = members?.map(m => ({
        id: m.id || m.user_id,
        name: m.name || m.user_name,
        email: m.email || m.user_email,
    })) || [];

    const filteredMembers = normalizedMembers.filter(m => 
        m.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        m.email?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    const selectedMembers = normalizedMembers.filter(m => selectedIds?.includes(m.id));

    const toggleMember = (memberId) => {
        if (disabled) return;
        if (selectedIds?.includes(memberId)) {
            onChange(selectedIds.filter(id => id !== memberId));
        } else {
            onChange([...(selectedIds || []), memberId]);
        }
    };

    return (
        <div className="relative" ref={dropdownRef}>
            <Button
                variant="outline"
                size="sm"
                onClick={() => !disabled && setIsOpen(!isOpen)}
                className={cn("h-8 min-w-[140px] justify-start text-left font-normal", disabled && "opacity-50")}
            >
                {selectedMembers.length === 0 ? (
                    <span className="text-brand-text-muted flex items-center gap-2">
                        <User className="w-3.5 h-3.5" />
                        Assign
                    </span>
                ) : (
                    <div className="flex items-center gap-1">
                        <div className="flex -space-x-1">
                            {selectedMembers.slice(0, 3).map(m => (
                                <div key={m.id} className="w-5 h-5 rounded-full bg-brand-primary text-white flex items-center justify-center text-[10px] font-medium border border-white">
                                    {getInitials(m.name)}
                                </div>
                            ))}
                        </div>
                        {selectedMembers.length > 3 && (
                            <span className="text-xs text-brand-text-muted ml-1">+{selectedMembers.length - 3}</span>
                        )}
                    </div>
                )}
                <ChevronDown className="w-3.5 h-3.5 ml-auto" />
            </Button>
            
            {isOpen && (
                <div className="absolute top-full left-0 mt-1 w-64 bg-white border border-brand-border rounded-lg shadow-lg z-50">
                    <div className="p-2 border-b border-brand-border">
                        <div className="relative">
                            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-brand-text-muted" />
                            <Input
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                placeholder="Search members..."
                                className="pl-7 h-8 text-sm"
                            />
                        </div>
                    </div>
                    <ScrollArea className="max-h-48">
                        <div className="p-1">
                            {filteredMembers.map(member => (
                                <button
                                    key={member.id}
                                    onClick={() => toggleMember(member.id)}
                                    className="w-full flex items-center gap-2 px-2 py-1.5 rounded hover:bg-brand-accent-soft text-left"
                                >
                                    <Checkbox checked={selectedIds?.includes(member.id)} className="pointer-events-none" />
                                    <div className="w-6 h-6 rounded-full bg-brand-primary text-white flex items-center justify-center text-xs">
                                        {getInitials(member.name)}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium truncate">{member.name}</p>
                                        <p className="text-xs text-brand-text-muted truncate">{member.email}</p>
                                    </div>
                                </button>
                            ))}
                        </div>
                    </ScrollArea>
                </div>
            )}
        </div>
    );
};

// File Icon Helper
const getFileIcon = (type) => {
    if (type?.startsWith('image/')) return <FileImage className="w-3 h-3" />;
    if (type?.startsWith('video/')) return <FileVideo className="w-3 h-3" />;
    return <File className="w-3 h-3" />;
};

export const TaskModal = ({ 
    isOpen, 
    onClose, 
    task = null, 
    listId, 
    statuses = [], 
    members = [],
    workspaceId,
    onTaskCreated,
    onTaskUpdated,
}) => {
    const { user } = useAuth();
    const fileInputRef = useRef(null);


    // Form state
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [status, setStatus] = useState('To Do');
    const [priority, setPriority] = useState('none');
    const [category, setCategory] = useState('');
    const [startDate, setStartDate] = useState(null);
    const [dueDate, setDueDate] = useState(null);
    const [timeEstimate, setTimeEstimate] = useState('');
    const [tags, setTags] = useState([]);
    const [newTag, setNewTag] = useState('');
    const [assigneeIds, setAssigneeIds] = useState([]);
    const [reviewerIds, setReviewerIds] = useState([]);
    const [checklist, setChecklist] = useState([]);
    const [newChecklistItem, setNewChecklistItem] = useState('');
    const [attachments, setAttachments] = useState([]);
    const [customFields, setCustomFields] = useState([]);
    const [customFieldValues, setCustomFieldValues] = useState({});
    const [dailyEndDate, setDailyEndDate] = useState(null);

    // Overload alert state
    const [overloadTasks, setOverloadTasks] = useState([]);
    const [overloadChecking, setOverloadChecking] = useState(false);

    // UI state
    const [saving, setSaving] = useState(false);
    const [loading, setLoading] = useState(false);
    const [uploadingFile, setUploadingFile] = useState(false);
    const [showCloseConfirm, setShowCloseConfirm] = useState(false);

    // Load task data if editing
    useEffect(() => {
        if (isOpen && task) {
            loadTaskData();
        } else if (isOpen && !task) {
            resetForm();
            loadCustomFields();
        }
    }, [isOpen, task]);

    const loadTaskData = async () => {
        setLoading(true);
        try {
            setTitle(task.title || '');
            setDescription(task.description || '');
            setStatus(task.status || 'To Do');
            setPriority(task.priority || 'none');
            setCategory(task.category || '');
            setStartDate(task.start_date ? new Date(task.start_date) : null);
            setDueDate(task.due_date ? new Date(task.due_date) : null);
            setTimeEstimate(task.time_estimate ? String(task.time_estimate) : '');
            setTags(task.tags || []);
            setChecklist(task.checklist || []);
            setCustomFieldValues(task.custom_field_values || {});
            setDailyEndDate(task.daily_end_date ? new Date(task.daily_end_date) : null);
            setAssigneeIds(task.assignee_ids || []);
            setReviewerIds(task.reviewer_ids || []);
            setAttachments(task.attachments || []);

            if (task.list_id) {
                const fieldsRes = await customFieldAPI.getByList(task.list_id);
                setCustomFields(fieldsRes.data);
            }
        } catch (error) {
            console.error('Failed to load task data:', error);
        } finally {
            setLoading(false);
        }
    };

    const loadCustomFields = async () => {
        if (listId) {
            try {
                const fieldsRes = await customFieldAPI.getByList(listId);
                setCustomFields(fieldsRes.data);
            } catch (error) {
                console.error('Failed to load custom fields:', error);
            }
        }
    };

    // Overload check — debounce 500ms after assigneeIds/startDate/dueDate change
    const overloadTimerRef = useRef(null);
    const overloadReqIdRef = useRef(0);

    useEffect(() => {
        // Clear previous overload when conditions not met
        if (!assigneeIds.length || !startDate || !dueDate || category === 'DAILY_TASK') {
            setOverloadTasks([]);
            setOverloadChecking(false);
            return;
        }

        setOverloadChecking(true);

        if (overloadTimerRef.current) clearTimeout(overloadTimerRef.current);

        overloadTimerRef.current = setTimeout(async () => {
            const reqId = ++overloadReqIdRef.current;
            const startStr = format(startDate, 'yyyy-MM-dd');
            const dueStr = format(dueDate, 'yyyy-MM-dd');
            const allOverloads = [];

            try {
                const results = await Promise.all(
                    assigneeIds.map(uid =>
                        taskAPI.checkOverload(workspaceId, {
                            assignee_id: uid,
                            start_date: startStr,
                            due_date: dueStr,
                            exclude_task_id: task?.id || undefined,
                        }).then(res => res.data.tasks || []).catch(() => [])
                    )
                );
                // Only process if this is still the latest request
                if (reqId !== overloadReqIdRef.current) return;

                for (const tasks of results) {
                    for (const t of tasks) {
                        if (!allOverloads.find(x => x.id === t.id)) {
                            allOverloads.push(t);
                        }
                    }
                }
                setOverloadTasks(allOverloads);
            } catch (error) {
                console.error('Overload check failed:', error);
            } finally {
                if (reqId === overloadReqIdRef.current) {
                    setOverloadChecking(false);
                }
            }
        }, 500);

        return () => {
            if (overloadTimerRef.current) clearTimeout(overloadTimerRef.current);
        };
    }, [assigneeIds, startDate, dueDate, category, workspaceId, task]);

    const GetDeafultStatusName = () => {
        const obj = statuses.filter(s => s.group === 'Not Started')?.sort((s1, s2) => s1.order - s2.order)[0]?.name;
        return obj || 'To Do';
    }

    const resetForm = () => {
        console.log('Resetting form...', GetDeafultStatusName());
        setTitle('');
        setDescription('');
        setStatus(GetDeafultStatusName());
        setPriority('none');
        setCategory('');
        setStartDate(null);
        setDueDate(null);
        setTimeEstimate('');
        setTags([]);
        setChecklist([]);
        setAssigneeIds([]);
        setReviewerIds([]);
        setCustomFieldValues({});
        setDailyEndDate(null);
        setAttachments([]);
        setNewTag('');
        setNewChecklistItem('');
        setOverloadTasks([]);
        setOverloadChecking(false);
        setShowCloseConfirm(false);
    };

    const handleSave = async () => {
        if (!title.trim()) {
            toast.error('Task title is required');
            return;
        }

        // Start date and Due date are mandatory for new tasks (not for DAILY_TASK)
        if (!task && category !== 'DAILY_TASK' && !startDate) {
            toast.error('Start date is required');
            return;
        }
        if (!task && category !== 'DAILY_TASK' && !dueDate) {
            toast.error('Due date is required');
            return;
        }
        if (!task && !listId) {
            toast.error('Please select a list first');
            return;
        }

        setSaving(true);
        try {
            const taskData = {
                title: title.trim(),
                description: description || null,
                status,
                priority,
                category: category || null,
                start_date: startDate ? format(startDate, 'yyyy-MM-dd') : null,
                due_date: dueDate ? format(dueDate, 'yyyy-MM-dd') : null,
                time_estimate: timeEstimate ? parseInt(timeEstimate) : null,
                tags: tags.length > 0 ? tags : [],
                checklist: checklist.length > 0 ? checklist : [],
                custom_field_values: Object.keys(customFieldValues).length > 0 ? customFieldValues : {},
                assignee_ids: assigneeIds,
                reviewer_ids: reviewerIds,
                attachments: attachments,
                daily_end_date: category === 'DAILY_TASK' && dailyEndDate ? format(dailyEndDate, 'yyyy-MM-dd') : null,
            };

            if (task) {
                await taskAPI.update(task.id, taskData);
                toast.success('Task updated');
                if (onTaskUpdated) onTaskUpdated();
            } else {
                const res = await taskAPI.create({ ...taskData, list_id: listId });
                const createdTask = res.data;
                toast.success('Task created');
                
                // If it's a BUG category, trigger AI analysis in background
                if (category === 'BUG' && createdTask?.id) {
                    setTimeout(async () => {
                        try {
                            const analysisRes = await bugAPI.analyzeSimilarity({
                                title: title.trim(),
                                description: description || '',
                                workspace_id: workspaceId
                            });
                            
                            if (analysisRes.data?.similar_bugs?.length > 0) {
                                // Update task with similar bugs
                                await taskAPI.update(createdTask.id, {
                                    similar_bugs: analysisRes.data.similar_bugs
                                });
                                toast.info(`🤖 AI found ${analysisRes.data.similar_bugs.length} similar bug(s). Check Bug Analysis section.`, { duration: 5000 });
                            }
                        } catch (err) {
                            console.error('Bug analysis failed:', err);
                            // Don't show error to user - this is background process
                        }
                    }, 500);
                }
                
                if (onTaskCreated) onTaskCreated(res.data);
            }
            
            onClose();
        } catch (error) {
            console.error('Task save error:', error);
            const detail = error.response?.data?.detail;
            toast.error(detail || 'Failed to save task');
        } finally {
            setSaving(false);
        }
    };

    // Tag handlers
    const handleAddTag = () => {
        if (newTag.trim() && !tags.includes(newTag.trim())) {
            setTags([...tags, newTag.trim()]);
            setNewTag('');
        }
    };

    const removeTag = (tagToRemove) => {
        setTags(tags.filter(t => t !== tagToRemove));
    };

    // Checklist handlers
    const handleAddChecklistItem = () => {
        if (newChecklistItem.trim()) {
            setChecklist([...checklist, { 
                id: Date.now().toString(),
                text: newChecklistItem.trim(), 
                completed: false 
            }]);
            setNewChecklistItem('');
        }
    };

    const toggleChecklistItem = (index) => {
        const updated = [...checklist];
        updated[index].completed = !updated[index].completed;
        setChecklist(updated);
    };

    const removeChecklistItem = (index) => {
        setChecklist(checklist.filter((_, i) => i !== index));
    };

    // File handlers
    const handleFileUpload = async (event) => {
        const file = event.target.files?.[0];
        if (!file) return;
        if (file.size > 50 * 1024 * 1024) {
            toast.error('Max 50MB');
            return;
        }
        setUploadingFile(true);
        try {
            const res = await fileAPI.upload(file);
            // Ensure we have a valid attachment object
            const attachment = {
                id: res.data.id,
                filename: res.data.filename,
                url: res.data.url,
                type: res.data.type || res.data.file_type || 'file'
            };
            setAttachments([...attachments, attachment]);
            toast.success('File uploaded');
        } catch (error) {
            console.error('Upload error:', error);
            const errorMessage = error.response?.data?.detail || error.message || 'Failed to upload';
            // Handle validation errors from FastAPI
            if (typeof errorMessage === 'object') {
                toast.error('Upload failed: Invalid request');
            } else {
                toast.error(errorMessage);
            }
        } finally {
            setUploadingFile(false);
            if (fileInputRef.current) fileInputRef.current.value = '';
        }
    };

    const isFormDirty = () => {
        return (
            title.trim() !== '' ||
            description !== '' ||
            status !== 'To Do' ||
            priority !== 'none' ||
            category !== '' ||
            startDate !== null ||
            dueDate !== null ||
            timeEstimate !== '' ||
            tags.length > 0 ||
            assigneeIds.length > 0 ||
            reviewerIds.length > 0 ||
            checklist.length > 0 ||
            attachments.length > 0 ||
            Object.keys(customFieldValues).length > 0
        );
    };

    const handleClose = () => {
        if (isFormDirty()) {
            setShowCloseConfirm(true);
            return;
        }
        resetForm();
        onClose();
    };

    if (!isOpen) return null;

    return (
        <Dialog open={isOpen} onOpenChange={handleClose}>
            <DialogContent className="max-w-4xl max-h-[90vh] p-0 overflow-hidden flex flex-col">
                {/* Sticky Header - Save button next to title */}
                <div className="flex items-center justify-between p-6 pb-4 border-b border-brand-border bg-white z-10 flex-shrink-0">
                    <Input
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="Task title"
                        className="text-xl font-semibold border-0 px-0 h-auto focus-visible:ring-0 placeholder:text-brand-text-muted"
                        data-testid="task-title-input"
                    />
                    <div className="flex items-center gap-2 ml-4 flex-shrink-0">
                        <Button
                            onClick={handleSave}
                            disabled={saving}
                            className="bg-brand-primary hover:bg-brand-primary-dark"
                            data-testid="task-save-btn"
                        >
                            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            {task ? 'Save Changes' : 'Create Task'}
                        </Button>
                    </div>
                </div>

                {/* Close Confirmation Popup */}
                {showCloseConfirm && (
                    <div className="absolute top-0 left-0 right-0 z-50 flex justify-center p-3">
                        <div className="bg-amber-50 border border-amber-300 rounded-lg shadow-lg px-4 py-3 flex items-center gap-3 max-w-md">
                            <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0" />
                            <span className="text-sm text-amber-800 flex-1">
                                Perubahan belum disimpan. Yakin ingin menutup?
                            </span>
                            <Button
                                variant="outline"
                                size="sm"
                                className="h-7 text-xs border-amber-400 text-amber-700 hover:bg-amber-100"
                                onClick={() => setShowCloseConfirm(false)}
                            >
                                Batal
                            </Button>
                            <Button
                                size="sm"
                                className="h-7 text-xs bg-amber-500 hover:bg-amber-600 text-white"
                                onClick={() => {
                                    setShowCloseConfirm(false);
                                    resetForm();
                                    onClose();
                                }}
                            >
                                Ya, Tutup
                            </Button>
                        </div>
                    </div>
                )}

                {/* Scrollable Content */}
                <div className="flex-1 overflow-y-auto">
                    <div className="p-6">
                        {loading ? (
                            <div className="flex items-center justify-center py-12">
                                <Loader2 className="w-8 h-8 animate-spin text-brand-primary" />
                            </div>
                        ) : (
                            <div className="space-y-4">
                                {/* Status & Priority Row */}
                                <div className="flex flex-wrap items-center gap-3">
                                    {/* Status */}
                                    <FieldStatus status={status} setStatus={setStatus} statuses={statuses} className="min-w-32 w-fit h-8" />

                                    {/* Priority */}
                                    <Select value={priority} onValueChange={setPriority}>
                                        <SelectTrigger className="w-32 h-8">
                                            <SelectValue>
                                                {priority === 'none' ? (
                                                    <span className="text-brand-text-muted">Priority</span>
                                                ) : (
                                                    <span className={cn("px-2 py-0.5 rounded text-xs", priorityConfig[priority]?.bgClass)}>
                                                        {priorityConfig[priority]?.label}
                                                    </span>
                                                )}
                                            </SelectValue>
                                        </SelectTrigger>
                                        <SelectContent>
                                            {Object.entries(priorityConfig).map(([key, config]) => (
                                                <SelectItem key={key} value={key}>
                                                    <span className={cn("px-2 py-0.5 rounded text-xs", config.bgClass)}>
                                                        {config.label}
                                                    </span>
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                {/* Properties Section */}
                                <div className="space-y-3 py-4 border-t border-brand-border">
                                    {/* Assignee */}
                                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                                        <div className="w-full sm:w-32 flex items-center gap-2 text-sm text-brand-text-muted">
                                            <User className="w-4 h-4" />
                                            Assignee
                                        </div>
                                        <div className="flex items-center gap-1">
                                            <AssigneeMultiSelect
                                                members={members}
                                                selectedIds={assigneeIds}
                                                onChange={setAssigneeIds}
                                            />
                                            {overloadChecking && (
                                                <Loader2 className="w-3.5 h-3.5 animate-spin text-brand-text-muted" />
                                            )}
                                            {!overloadChecking && overloadTasks.length > 0 && (() => {
                                                const count = overloadTasks.length;
                                                // 1-5: NICE (green), 6-8: WARNING (yellow), >8: OVERLOAD (red)
                                                let level, bgClass, textClass, borderClass, Icon, headerClass, itemBorderClass, itemBgClass;
                                                if (count <= 5) {
                                                    level = 'NICE';
                                                    bgClass = 'bg-green-100 hover:bg-green-200';
                                                    textClass = 'text-green-600';
                                                    borderClass = 'border-green-100';
                                                    headerClass = 'text-green-600 border-green-100';
                                                    itemBorderClass = 'border-green-100';
                                                    itemBgClass = 'bg-green-50/50';
                                                    Icon = CheckCircle;
                                                } else if (count <= 8) {
                                                    level = 'WARNING';
                                                    bgClass = 'bg-yellow-100 hover:bg-yellow-200';
                                                    textClass = 'text-yellow-600';
                                                    borderClass = 'border-yellow-100';
                                                    headerClass = 'text-yellow-600 border-yellow-100';
                                                    itemBorderClass = 'border-yellow-100';
                                                    itemBgClass = 'bg-yellow-50/50';
                                                    Icon = AlertTriangle;
                                                } else {
                                                    level = 'OVERLOAD';
                                                    bgClass = 'bg-red-100 hover:bg-red-200';
                                                    textClass = 'text-red-600';
                                                    borderClass = 'border-red-100';
                                                    headerClass = 'text-red-600 border-red-100';
                                                    itemBorderClass = 'border-red-100';
                                                    itemBgClass = 'bg-red-50/50';
                                                    Icon = AlertTriangle;
                                                }
                                                return (
                                                <Popover>
                                                    <PopoverTrigger asChild>
                                                        <button
                                                            type="button"
                                                            className={`inline-flex items-center justify-center w-5 h-5 rounded-full ${bgClass} ${textClass} transition-colors flex-shrink-0`}
                                                            title={`${count} task(s) in this date range — ${level}`}
                                                        >
                                                            <Icon className="w-3.5 h-3.5" />
                                                        </button>
                                                    </PopoverTrigger>
                                                    <PopoverContent className="w-80 p-0" align="start">
                                                        <div className="space-y-2 p-3">
                                                            <h4 className={`font-semibold text-sm ${headerClass} border-b pb-2`}>
                                                                {level} — {count} task(s) in this date range
                                                            </h4>
                                                            <div className="max-h-60 overflow-y-auto space-y-1.5">
                                                                {overloadTasks.map(t => (
                                                                    <div key={t.id} className={`text-xs space-y-1 p-2 rounded border ${itemBorderClass} ${itemBgClass}`}>
                                                                        <div className="flex items-center gap-2 font-medium">
                                                                            <span className="text-brand-primary flex-shrink-0">{t.task_code}</span>
                                                                            <span className="truncate">{t.title}</span>
                                                                        </div>
                                                                        <div className="flex gap-3 text-gray-500">
                                                                            <span>Start: {t.start_date}</span>
                                                                            <span>Due: {t.due_date}</span>
                                                                        </div>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </PopoverContent>
                                                </Popover>
                                                );
                                            })()}
                                        </div>
                                    </div>

                                    {/* Reviewer */}
                                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                                        <div className="w-full sm:w-32 flex items-center gap-2 text-sm text-brand-text-muted">
                                            <Eye className="w-4 h-4" />
                                            Review By
                                        </div>
                                        <AssigneeMultiSelect
                                            members={members}
                                            selectedIds={reviewerIds}
                                            onChange={setReviewerIds}
                                        />
                                    </div>

                                    {/* Timeline - Start Date & Due Date (hidden for DAILY_TASK) */}
                                    {category !== 'DAILY_TASK' && (
                                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                                        <div className="w-full sm:w-32 flex items-center gap-2 text-sm text-brand-text-muted">
                                            <CalendarIcon className="w-4 h-4" />
                                            Timeline <span className="text-red-500">*</span>
                                        </div>
                                        <div className="flex items-center gap-2 flex-wrap">
                                            {/* Start Date */}
                                            <Popover>
                                                <PopoverTrigger asChild>
                                                    <Button 
                                                        variant="outline" 
                                                        size="sm" 
                                                        className={cn(
                                                            "h-8 px-3",
                                                            !startDate && "text-brand-text-muted border-dashed",
                                                            !task && !startDate && "border-red-300"
                                                        )}
                                                        data-testid="task-start-date-btn"
                                                    >
                                                        <CalendarIcon className="w-3 h-3 mr-1.5" />
                                                        {startDate ? format(startDate, 'MMM dd, yyyy') : 'Start Date *'}
                                                    </Button>
                                                </PopoverTrigger>
                                                <PopoverContent className="w-auto p-0" align="start">
                                                    <Calendar
                                                        mode="single"
                                                        selected={startDate}
                                                        onSelect={setStartDate}
                                                    />
                                                </PopoverContent>
                                            </Popover>
                                            
                                            <ChevronRight className="w-4 h-4 text-brand-text-muted" />
                                            
                                            {/* Due Date */}
                                            <Popover>
                                                <PopoverTrigger asChild>
                                                    <Button 
                                                        variant="outline" 
                                                        size="sm" 
                                                        className={cn(
                                                            "h-8 px-3",
                                                            !dueDate && "text-brand-text-muted border-dashed",
                                                            !task && !dueDate && "border-red-300"
                                                        )}
                                                        data-testid="task-due-date-btn"
                                                    >
                                                        <CalendarIcon className="w-3 h-3 mr-1.5" />
                                                        {dueDate ? format(dueDate, 'MMM dd, yyyy') : 'Due Date *'}
                                                    </Button>
                                                </PopoverTrigger>
                                                <PopoverContent className="w-auto p-0" align="start">
                                                    <Calendar
                                                        mode="single"
                                                        selected={dueDate}
                                                        onSelect={setDueDate}
                                                    />
                                                </PopoverContent>
                                            </Popover>
                                        </div>
                                    </div>
                                    )}

                                    {/* Category */}
                                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                                        <div className="w-full sm:w-32 flex items-center gap-2 text-sm text-brand-text-muted">
                                            <Bug className="w-4 h-4" />
                                            Category
                                        </div>
                                        <Select value={category || 'none'} onValueChange={(val) => setCategory(val === 'none' ? '' : val)}>
                                            <SelectTrigger className="w-full sm:w-40 h-8 border-0 bg-transparent hover:bg-brand-accent-soft">
                                                <SelectValue>
                                                    {!category ? (
                                                        <span className="text-brand-text-muted">None</span>
                                                    ) : (
                                                        <span className="px-2 py-0.5 rounded text-xs bg-brand-accent-soft text-brand-primary">
                                                            {category}
                                                        </span>
                                                    )}
                                                </SelectValue>
                                            </SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="none">
                                                    <span className="text-brand-text-muted">None</span>
                                                </SelectItem>
                                                {taskCategories.map((cat) => (
                                                    <SelectItem key={cat} value={cat}>
                                                        <span className="px-2 py-0.5 rounded text-xs bg-brand-accent-soft text-brand-primary">
                                                            {cat}
                                                        </span>
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>

                                    {/* Daily End Date — only for DAILY_TASK category */}
                                    {category === 'DAILY_TASK' && (
                                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                                        <div className="w-full sm:w-32 flex items-center gap-2 text-sm text-brand-text-muted">
                                            <CalendarIcon className="w-4 h-4" />
                                            Sampai Tanggal
                                        </div>
                                        <Popover>
                                            <PopoverTrigger asChild>
                                                <Button variant="outline" className="w-full sm:w-40 h-8 justify-start text-left font-normal">
                                                    <CalendarIcon className="w-3.5 h-3.5 mr-1" />
                                                    {dailyEndDate ? format(dailyEndDate, 'dd MMM yyyy') : <span className="text-brand-text-muted">Pilih tanggal</span>}
                                                </Button>
                                            </PopoverTrigger>
                                            <PopoverContent className="w-auto p-0" align="start">
                                                <Calendar
                                                    mode="single"
                                                    selected={dailyEndDate}
                                                    onSelect={setDailyEndDate}
                                                    initialFocus
                                                />
                                            </PopoverContent>
                                        </Popover>
                                        {dailyEndDate && (
                                            <Button variant="ghost" size="sm" className="h-8 px-2" onClick={() => setDailyEndDate(null)}>
                                                Clear
                                            </Button>
                                        )}
                                    </div>
                                    )}

                                    {/* Time Estimate */}
                                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                                        <div className="w-full sm:w-32 flex items-center gap-2 text-sm text-brand-text-muted">
                                            <Clock className="w-4 h-4" />
                                            Estimate
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <Input
                                                type="number"
                                                value={timeEstimate}
                                                onChange={(e) => setTimeEstimate(e.target.value)}
                                                placeholder="Minutes"
                                                className="w-24 h-8"
                                                data-testid="task-estimate-input"
                                            />
                                            <span className="text-sm text-brand-text-muted">mins</span>
                                        </div>
                                    </div>

                                    {/* Tags */}
                                    <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                                        <div className="w-full sm:w-32 flex items-center gap-2 text-sm text-brand-text-muted">
                                            <Tag className="w-4 h-4" />
                                            Tags
                                        </div>
                                        <div className="flex items-center gap-2 flex-wrap flex-1">
                                            {tags.map(tag => (
                                                <span 
                                                    key={tag}
                                                    className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-800 text-white rounded text-xs"
                                                >
                                                    {tag}
                                                    <button onClick={() => removeTag(tag)} className="hover:text-red-300">
                                                        <X className="w-3 h-3" />
                                                    </button>
                                                </span>
                                            ))}
                                            <Input
                                                value={newTag}
                                                onChange={(e) => setNewTag(e.target.value)}
                                                placeholder="+ Add tag"
                                                className="w-24 h-6 text-xs border-0 bg-transparent px-1"
                                                onKeyDown={(e) => e.key === 'Enter' && handleAddTag()}
                                            />
                                        </div>
                                    </div>

                                    {/* Attachments */}
                                    <div className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-4">
                                        <div className="w-full sm:w-32 flex items-center gap-2 text-sm text-brand-text-muted pt-1">
                                            <Paperclip className="w-4 h-4" />
                                            Attachments
                                        </div>
                                        <div className="flex-1">
                                            {attachments.length > 0 && (
                                                <div className="flex flex-wrap gap-2 mb-2">
                                                    {attachments.map((att, idx) => (
                                                        <div key={att.id || idx} className="flex items-center gap-1 px-2 py-1 bg-brand-accent-soft rounded text-xs max-w-full">
                                                            {getFileIcon(att.type || att.file_type || 'file')}
                                                            <a href={att.url} target="_blank" rel="noopener noreferrer" className="hover:underline truncate max-w-[150px]">
                                                                {att.filename || 'File'}
                                                            </a>
                                                            <button onClick={() => setAttachments(attachments.filter((a, i) => att.id ? a.id !== att.id : i !== idx))} className="hover:text-red-500 flex-shrink-0">
                                                                <X className="w-3 h-3" />
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}
                                            <input type="file" ref={fileInputRef} onChange={handleFileUpload} className="hidden" />
                                            <Button variant="ghost" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploadingFile}>
                                                {uploadingFile ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Upload className="w-4 h-4 mr-1" />}
                                                Upload
                                            </Button>
                                        </div>
                                    </div>
                                </div>

                                {/* Description */}
                                <div className="pt-4 border-t border-brand-border">
                                    <Label className="text-sm text-brand-text-muted mb-2 block">Description</Label>
                                    <RichTextEditor
                                        value={description}
                                        onChange={(val) => setDescription(val || '')}
                                        placeholder="Write your description here..."
                                        minHeight="120px"
                                    />
                                </div>

                                {/* Checklist */}
                                <div className="pt-4 border-t border-brand-border">
                                    <Label className="text-sm text-brand-text-muted mb-2 flex items-center gap-2">
                                        <ListChecks className="w-4 h-4" />
                                        Checklist
                                    </Label>
                                    <div className="space-y-2">
                                        {checklist.map((item, index) => (
                                            <div key={index} className="flex items-center gap-2">
                                                <Checkbox
                                                    checked={item.completed}
                                                    onCheckedChange={() => toggleChecklistItem(index)}
                                                />
                                                <span className={cn(
                                                    "text-sm flex-1",
                                                    item.completed && "line-through text-brand-text-muted"
                                                )}>
                                                    {item.text}
                                                </span>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    className="h-6 w-6"
                                                    onClick={() => removeChecklistItem(index)}
                                                >
                                                    <Trash2 className="w-3 h-3 text-brand-text-muted hover:text-red-500" />
                                                </Button>
                                            </div>
                                        ))}
                                        <div className="flex items-center gap-2">
                                            <Plus className="w-4 h-4 text-brand-text-muted" />
                                            <Input
                                                value={newChecklistItem}
                                                onChange={(e) => setNewChecklistItem(e.target.value)}
                                                placeholder="Add checklist item"
                                                className="h-8 text-sm flex-1"
                                                onKeyDown={(e) => e.key === 'Enter' && handleAddChecklistItem()}
                                            />
                                        </div>
                                    </div>
                                </div>

                                {/* Custom Fields */}
                                {customFields.length > 0 && (
                                    <div className="pt-4 border-t border-brand-border">
                                        <Label className="text-sm text-brand-text-muted mb-3 block">Custom Fields</Label>
                                        <div className="space-y-3">
                                            {customFields.map(field => (
                                                <div key={field.id} className="flex flex-col sm:flex-row sm:items-center gap-2">
                                                    <Label className="w-full sm:w-32 text-sm">{field.name}</Label>
                                                    {field.type === 'dropdown' ? (
                                                        <Select
                                                            value={customFieldValues[field.id] || ''}
                                                            onValueChange={(val) => setCustomFieldValues({...customFieldValues, [field.id]: val})}
                                                        >
                                                            <SelectTrigger className="w-full sm:w-48 h-8">
                                                                <SelectValue placeholder="Select..." />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                {field.options?.map(opt => (
                                                                    <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                                                                ))}
                                                            </SelectContent>
                                                        </Select>
                                                    ) : field.type === 'checkbox' ? (
                                                        <Checkbox
                                                            checked={customFieldValues[field.id] || false}
                                                            onCheckedChange={(val) => setCustomFieldValues({...customFieldValues, [field.id]: val})}
                                                        />
                                                    ) : (
                                                        <Input
                                                            type={field.type === 'number' ? 'number' : 'text'}
                                                            value={customFieldValues[field.id] || ''}
                                                            onChange={(e) => setCustomFieldValues({...customFieldValues, [field.id]: e.target.value})}
                                                            className="w-full sm:w-48 h-8"
                                                        />
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default TaskModal;
